import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ScrollText, User, Clock, Filter } from "lucide-react";

const ACTION_LABELS = {
  "user.create": "User Created",
  "user.update": "User Updated",
  "user.delete": "User Deleted",
  "user.bulk_create": "Bulk Created",
  "user.bulk_update": "Bulk Updated",
  "engagement.note_add": "Note Added",
  "engagement.issue_add": "Issue Raised",
  "engagement.cluster_set": "Cluster Changed",
  "engagement.scope_set": "Scope Updated",
};

const ACTION_COLORS = {
  "user.create": "var(--on-track)",
  "user.update": "var(--accent)",
  "user.delete": "var(--behind)",
  "user.bulk_create": "var(--on-track)",
  "user.bulk_update": "var(--amber)",
  "engagement.note_add": "var(--on-track)",
  "engagement.issue_add": "var(--amber)",
  "engagement.cluster_set": "var(--accent)",
};

const FIELD_LABEL = {
  role: "Role",
  menu_permissions: "Menu Access",
  country_permissions: "Country Access",
  cluster_permissions: "Cluster Access",
  can_edit: "Edit Access",
  name: "Name",
  password: "Password",
  csv_row: "CSV Row",
  cluster: "Cluster",
  note: "Note",
  issue: "Issue",
};

function fmtVal(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(none)";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");

  useEffect(() => {
    api.get("/audit-logs", { params: { limit: 500 } })
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => logs.filter((l) => {
    if (actionFilter && l.action !== actionFilter) return false;
    if (actorFilter && !(l.actor_email || "").toLowerCase().includes(actorFilter.toLowerCase())) return false;
    return true;
  }), [logs, actionFilter, actorFilter]);

  const uniqueActions = Array.from(new Set(logs.map((l) => l.action))).sort();

  return (
    <div className="max-w-[1400px] mx-auto px-10 py-10 fade-in-up" data-testid="audit-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Admin · Access Audit Trail
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Audit Log</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          Every user permission change is recorded with the admin who made it, before/after values,
          and a precise timestamp. This log is admin-only and append-only.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select className="input-flat w-56" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="audit-action-filter">
          <option value="">All Actions</option>
          {uniqueActions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <input
          className="input-flat flex-1"
          placeholder="Filter by admin email…"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          data-testid="audit-actor-filter"
        />
        <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => { setActionFilter(""); setActorFilter(""); }}>
          <Filter size={12} /> Clear
        </button>
      </div>

      <div className="mono text-[11px] text-[color:var(--ink-2)] mb-3 uppercase tracking-widest">
        {loading ? "Loading…" : `${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`}
      </div>

      <div className="space-y-2">
        {filtered.map((l) => (
          <div key={l.id} className="hairline bg-white" data-testid={`log-${l.id}`}>
            <div className="flex items-start gap-4 px-4 py-3 flex-wrap">
              <div
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 border w-fit"
                style={{ borderColor: ACTION_COLORS[l.action], color: ACTION_COLORS[l.action] }}
              >
                {ACTION_LABELS[l.action] || l.action}
              </div>
              <div className="flex-1 min-w-64">
                <div className="text-sm">
                  <span className="font-medium">{l.actor_email}</span>
                  <span className="text-[color:var(--ink-2)]"> changed </span>
                  <span className="font-medium">{l.target_label}</span>
                </div>
                <div className="mono text-[10px] text-[color:var(--ink-3)] flex items-center gap-3 mt-0.5">
                  <span className="flex items-center gap-1"><Clock size={10} /> {new Date(l.timestamp).toLocaleString()}</span>
                </div>
              </div>
              {Object.keys(l.changes || {}).length > 0 && (
                <div className="w-full pt-2 mt-2 border-t border-[color:var(--border)] grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(l.changes).map(([field, diff]) => (
                    <div key={field} className="text-xs border border-[color:var(--border)] p-2 bg-[color:var(--surface-2)]">
                      <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">
                        {FIELD_LABEL[field] || field}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[9px] uppercase text-[color:var(--ink-3)]">Old</div>
                          <div className="text-[color:var(--behind)] break-words">{fmtVal(diff.old)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-[color:var(--ink-3)]">New</div>
                          <div className="text-[color:var(--on-track)] break-words">{fmtVal(diff.new)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <div className="hairline bg-white p-10 text-center">
            <ScrollText size={22} className="mx-auto mb-3 text-[color:var(--ink-3)]" />
            <div className="serif text-xl">No audit entries yet</div>
            <div className="text-sm text-[color:var(--ink-2)] mt-1">Changes will appear here as they happen.</div>
          </div>
        )}
      </div>
    </div>
  );
}
